import React, { useEffect, useState, memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { Plus, Search, BookOpen, FileText, Clock, Database, Bot, ArrowUpRight, Trash2, MoreVertical } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { DBKnowledgeBaseThunks, knowledgeBaseSelectByQuery } from '@common-reducers/DBServiceThunks';
import { formatDistanceToNow, type Locale } from 'date-fns';
import { arSA, de, enUS, es, fr, he, hi, it, pt } from 'date-fns/locale';
import { KnowledgeBase } from '@common/models/knowledge-base';
import { selectKnowledgeBaseDocumentCount, selectKnowledgeBaseTotalSize } from '@common/reducers/KnowledgeBaseSelectors';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { AnalyticsService } from '@common/services/analytics-service';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';

const DATE_FNS_LOCALE_BY_LANG: Record<string, Locale> = {
  ar: arSA,
  de,
  en: enUS,
  es,
  fr,
  he,
  hi,
  it,
  pt,
};

// Utility function for merging class names
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Utility function to format bytes into human readable format
export const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

// Memoized search input component to prevent losing focus
const SearchInput = memo(({
  value,
  onChange,
  placeholder
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
}) => {
  return (
    <div className="relative w-full md:w-[600px] md:flex-auto">
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        //className="w-full h-12 pl-12 pr-6 bg-white border border-gray-200 rounded-xl text-lg shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        style={{
          width: '100%',
          height: '48px',
          paddingLeft: '48px',
          paddingRight: '24px',
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '12px',
          fontSize: '1.125rem',
          color: 'var(--color-text)',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          transition: 'all 200ms',
        }}
      />
    </div>
  );
});
SearchInput.displayName = 'SearchInput';

// Memoized status filter component
const StatusFilter = memo(({
  value,
  onChange,
  placeholder
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  placeholder: string;
}) => {
  return (
    <select
      value={value}
      onChange={onChange}
      className="w-[240px] h-12 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 px-6 text-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    >
      <option value="all">{placeholder}</option>
    </select>
  );
});
StatusFilter.displayName = 'StatusFilter';

// Knowledge Base Card Component
const KnowledgeBaseCard = ({
  kb,
  onClick,
  t,
  relativeDateLocale
}: {
  kb: KnowledgeBase;
  onClick: (kb: KnowledgeBase) => void;
  t: any;
  relativeDateLocale: Locale;
}) => {
  const dispatch = useAppDispatch();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const analytics = useMemo(() => new AnalyticsService(), []);

  // Format the last updated date
  let updatedDate = t('knowledgeBase.unknown');
  if (kb.lastUpdated) {
    try {
      updatedDate = formatDistanceToNow(new Date(kb.lastUpdated), { addSuffix: true, locale: relativeDateLocale });
    } catch (e) {
      console.error('Error formatting date:', e);
    }
  }

  const documentCount = useAppSelector(state =>
    kb._id ? selectKnowledgeBaseDocumentCount(state, kb._id) : 0
  );

  // Get the total size of documents for this knowledge base
  const totalSize = useAppSelector(state =>
    kb._id ? selectKnowledgeBaseTotalSize(state, kb._id) : 0
  );

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Track delete dialog opening
    analytics.event('kb_delete_initiated', {
      category: 'knowledge_base_management',
      action: 'delete_dialog_opened',
      kb_id: kb._id,
      kb_name: kb.name,
      document_count: documentCount,
      total_size_bytes: totalSize
    });
    
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!kb._id) return;

    // Track delete confirmation
    analytics.event('kb_delete_confirmed', {
      category: 'knowledge_base_management',
      action: 'delete_confirmed',
      kb_id: kb._id,
      kb_name: kb.name,
      document_count: documentCount,
      total_size_bytes: totalSize
    });

    setIsDeleting(true);
    try {
      await dispatch(DBKnowledgeBaseThunks.delete({
        entity: { _id: kb._id }
      })).unwrap();

      // Track successful deletion
      analytics.event('kb_delete_success', {
        category: 'knowledge_base_management',
        action: 'delete_completed',
        kb_id: kb._id,
        kb_name: kb.name,
        document_count: documentCount,
        total_size_bytes: totalSize
      });

      toast.success(t('knowledgeBase.deleteSuccess'), {
        description: t('knowledgeBase.deleteSuccessDescription', { name: kb.name })
      });

      // Refresh the knowledge base list
      dispatch(DBKnowledgeBaseThunks.find({}));
    } catch (error: any) {
      console.error('Failed to delete knowledge base:', error);
      
      // Track deletion failure
      analytics.event('kb_delete_error', {
        category: 'knowledge_base_management',
        action: 'delete_failed',
        kb_id: kb._id,
        kb_name: kb.name,
        error: error.message || t('knowledgeBase.unknown')
      });
      
      toast.error(t('knowledgeBase.deleteError'), {
        description: error.message || t('knowledgeBase.deleteErrorDescription')
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  return (
    <>
      <div
        onClick={() => onClick(kb)}
        className="transition-all duration-200 rounded-2xl overflow-hidden cursor-pointer relative group"
        style={{
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)'
        }}
      >
        <div className="p-5 md:p-8">
          {/* Header with knowledge base info */}
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-start gap-4 min-w-0 flex-1">
              <div className="p-3 rounded-xl self-center flex-shrink-0" style={{ backgroundColor: 'var(--color-surface-accent)' }}>
                <BookOpen className="w-6 h-6" style={{ color: 'var(--color-primary)' }} />
              </div>
              <div className="min-w-0 flex-1 overflow-hidden">
                <div className="text-xl font-medium truncate" style={{ color: 'var(--color-text)' }}>{kb.name}</div>
                <p className="text-base truncate" style={{ color: 'var(--color-text-muted)' }}>{kb.description}</p>
              </div>
            </div>
            <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreVertical className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="cursor-pointer"
                    style={{ color: 'var(--color-danger)' }}
                    onClick={handleDeleteClick}
                  >
                    <Trash2 className="w-4 h-4" />
                    {t('common.delete')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6 mt-8 mb-6">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
              <div>
                <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{t('knowledgeBase.documents')}</p>
                <p className="text-lg font-medium" style={{ color: 'var(--color-text)' }}>{documentCount}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Database className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
              <div>
                <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{t('knowledgeBase.totalSize')}</p>
                <p className="text-lg font-medium" style={{ color: 'var(--color-text)' }}>{formatBytes(totalSize)}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-6" style={{ borderTop: '1px solid var(--color-border)' }}>
            <div>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{t('knowledgeBase.lastUpdated')}</p>
              <p className="text-base font-medium" style={{ color: 'var(--color-text)' }}>
                {updatedDate}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-base font-medium" style={{ color: 'var(--color-link)' }}>{t('knowledgeBase.botsCount', { count: kb.associatedBots.length })}</span>
              <ArrowUpRight className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('knowledgeBase.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('knowledgeBase.deleteDescription', { name: kb.name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              {t('knowledgeBase.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? t('knowledgeBase.deleting') : t('knowledgeBase.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const KnowledgeBaseContent = () => {
  const { t, i18n } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const analytics = useMemo(() => new AnalyticsService(), []);
  const relativeDateLocale = DATE_FNS_LOCALE_BY_LANG[i18n.language.split('-')[0].toLowerCase()] || enUS;

  // Fetch knowledge bases on component mount
  useEffect(() => {
    dispatch(DBKnowledgeBaseThunks.find({}));
  }, [dispatch]);

  // Select and filter knowledge bases from store
  const knowledgeBases = useAppSelector(state => {
    if (!state.DBKnowledgeBaseReducer?.ids) {
      return [];
    }

    const query = {
      ...(searchQuery && {
        $or: [
          { name: { $regex: searchQuery, $options: 'i' } },
          { description: { $regex: searchQuery, $options: 'i' } }
        ]
      })
    };
    return knowledgeBaseSelectByQuery(state.DBKnowledgeBaseReducer, query);
  });

  // Get loading state
  const isLoading = useAppSelector(state =>
    state.DBKnowledgeBaseReducer?.itemsLoading ?? false
  );

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  }, []);

  const handleKnowledgeBaseClick = useCallback((kb: KnowledgeBase) => {
    // Track knowledge base detail view
    analytics.event('kb_detail_viewed', {
      category: 'knowledge_base_navigation',
      action: 'view_kb_details',
      kb_id: kb._id,
      kb_name: kb.name
    });
    
    navigate(`/knowledge-base/${kb._id}`);
  }, [navigate, analytics]);

  return (
    <>
      <div className="h-full overflow-auto p-4 md:p-12" style={{ backgroundColor: 'var(--color-bg)' }}>
        <div className="max-w-[1600px] mx-auto space-y-6 md:space-y-12">
          {/* Filters */}
          <div className="flex justify-between gap-2">
            <SearchInput value={searchQuery} onChange={handleSearchChange} placeholder={t('knowledgeBase.searchPlaceholder')} />
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center items-center min-h-[400px]">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--color-link)' }}></div>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && knowledgeBases.length === 0 && (
            <div className="flex justify-center items-center min-h-[400px]">
              <p style={{ color: 'var(--color-text-muted)' }}>{t('knowledgeBase.emptyState')}</p>
            </div>
          )}

          {/* Knowledge Base Grid */}
          {!isLoading && knowledgeBases.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
              {knowledgeBases.map((kb) => (
                <KnowledgeBaseCard
                  key={kb._id}
                  kb={kb}
                  onClick={handleKnowledgeBaseClick}
                  t={t}
                  relativeDateLocale={relativeDateLocale}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default KnowledgeBaseContent; 
